/**
 * Алгоритм раскладки Git-графа
 * Размещает коммиты на плоскости с учётом веток и merge-коммитов
 */

import type { CommitNode } from '@/types/git';
import type {
  NodePosition,
  CommitNodeVisual,
  CommitEdge,
  BranchInfo,
  GraphState,
  GraphConfig,
  BranchColor,
} from './types';
import { BRANCH_COLORS, DEFAULT_GRAPH_CONFIG } from './types';

/** Определяет тип ветки по названию */
function getBranchType(branchName: string): BranchColor {
  const lowerName = branchName.toLowerCase();

  if (lowerName === 'main' || lowerName === 'master') {
    return 'main';
  }
  if (lowerName.startsWith('feature/') || lowerName.startsWith('feat/')) {
    return 'feature';
  }
  if (lowerName === 'develop' || lowerName.startsWith('dev')) {
    return 'develop';
  }
  if (lowerName.startsWith('hotfix/')) {
    return 'hotfix';
  }
  if (lowerName.startsWith('release/')) {
    return 'release';
  }

  return 'custom';
}

/** Создаёт информацию о ветке */
function createBranchInfo(branchName: string, isRemote: boolean = false): BranchInfo {
  const type = getBranchType(branchName);
  return {
    name: branchName,
    color: type,
    hexColor: BRANCH_COLORS[type],
    isRemote,
  };
}

/** Топологическая сортировка коммитов (от новых к старым) */
function topologicalSort(commits: Record<string, CommitNode>, headSha: string): CommitNode[] {
  const result: CommitNode[] = [];
  const visited = new Set<string>();
  const commitsMap = commits;

  function visit(sha: string) {
    if (visited.has(sha)) return;
    visited.add(sha);

    const commit = commitsMap[sha];
    if (!commit) return;

    // Сначала посещаем родителей (более старые коммиты)
    for (const parentSha of commit.parents) {
      visit(parentSha);
    }

    result.push(commit);
  }

  visit(headSha);

  // Разворачиваем, чтобы новые коммиты были в начале
  return result.reverse();
}

/** Определяет ветку для каждого коммита */
function assignBranchesToCommits(
  commits: CommitNode[],
  branches: Record<string, string>,
  _headSha: string,
  currentBranch: string | null
): Map<string, BranchInfo> {
  const commitBranches = new Map<string, BranchInfo>();

  // Создаём обратное отображение: sha -> [branchNames]
  const shaToBranches = new Map<string, string[]>();
  for (const [branchName, sha] of Object.entries(branches)) {
    if (!shaToBranches.has(sha)) {
      shaToBranches.set(sha, []);
    }
    shaToBranches.get(sha)!.push(branchName);
  }

  // Определяем основную ветку для отслеживания lineage
  const mainBranch = currentBranch || 'main';

  // Проходим по коммитам и назначаем ветки
  const branchLineage = new Map<string, string>(); // sha -> branchName

  for (const commit of commits) {
    // Проверяем, есть ли у этого коммита метка ветки
    const branchLabels = shaToBranches.get(commit.sha) || [];

    if (branchLabels.length > 0) {
      // Берём первую ветку как основную для этого коммита
      const primaryBranch = branchLabels[0] ?? mainBranch;
      commitBranches.set(commit.sha, createBranchInfo(primaryBranch));
      branchLineage.set(commit.sha, primaryBranch);
    } else {
      // Наследуем ветку от первого родителя
      const firstParent = commit.parents[0];
      if (firstParent && branchLineage.has(firstParent)) {
        const parentBranch = branchLineage.get(firstParent) ?? mainBranch;
        commitBranches.set(commit.sha, createBranchInfo(parentBranch));
        branchLineage.set(commit.sha, parentBranch);
      } else {
        // Если нет родителя или у родителя нет ветки, используем текущую
        commitBranches.set(commit.sha, createBranchInfo(mainBranch));
        branchLineage.set(commit.sha, mainBranch);
      }
    }
  }

  return commitBranches;
}

/** Вычисляет позиции узлов */
function calculatePositions(
  commits: CommitNode[],
  commitBranches: Map<string, BranchInfo>,
  branches: Record<string, string>,
  config: GraphConfig
): Map<string, NodePosition> {
  const positions = new Map<string, NodePosition>();

  // Определяем lane (колонку) для каждой ветки
  const branchLanes = new Map<string, number>();
  const sortedBranches = Object.keys(branches).sort((a, b) => {
    // main/master всегда в колонке 0
    if (a === 'main' || a === 'master') return -1;
    if (b === 'main' || b === 'master') return 1;
    // develop в колонке 1
    if (a === 'develop') return -1;
    if (b === 'develop') return 1;
    return a.localeCompare(b);
  });

  sortedBranches.forEach((branchName, index) => {
    branchLanes.set(branchName, index);
  });

  // Вычисляем позиции для каждого коммита
  commits.forEach((commit, index) => {
    const branchInfo = commitBranches.get(commit.sha);
    const lane = branchInfo ? (branchLanes.get(branchInfo.name) ?? 0) : 0;

    positions.set(commit.sha, {
      x: config.paddingLeft + lane * config.branchSpacingX,
      y: config.paddingTop + index * config.nodeSpacingY,
    });
  });

  return positions;
}

/** Создаёт рёбра между коммитами */
function createEdges(
  commits: CommitNode[],
  positions: Map<string, NodePosition>,
  commitBranches: Map<string, BranchInfo>
): CommitEdge[] {
  const edges: CommitEdge[] = [];

  for (const commit of commits) {
    const toPosition = positions.get(commit.sha);
    if (!toPosition) continue;

    const branchInfo = commitBranches.get(commit.sha);
    const color = branchInfo?.hexColor || BRANCH_COLORS.custom;

    for (const parentSha of commit.parents) {
      const fromPosition = positions.get(parentSha);
      if (!fromPosition) continue;

      edges.push({
        fromSha: parentSha,
        toSha: commit.sha,
        fromPosition,
        toPosition,
        color,
        isMerge: commit.parents.length > 1,
      });
    }
  }

  return edges;
}

/** Генерирует путь SVG для ребра (кривая Безье) */
export function generateEdgePath(
  from: NodePosition,
  to: NodePosition,
  isMerge: boolean = false
): string {
  // Для merge-коммитов используем более плавные кривые
  const midY = (from.y + to.y) / 2;

  if (isMerge) {
    // Кривая с дополнительным изгибом для merge
    const controlPoint1Y = from.y + (midY - from.y) * 0.8;
    const controlPoint2Y = to.y - (to.y - midY) * 0.8;

    return `M ${from.x} ${from.y} 
            C ${from.x} ${controlPoint1Y}, 
              ${to.x} ${controlPoint2Y}, 
              ${to.x} ${to.y}`;
  }

  // Простая кривая для обычных коммитов
  return `M ${from.x} ${from.y} 
          C ${from.x} ${midY}, 
            ${to.x} ${midY}, 
            ${to.x} ${to.y}`;
}

/** Основная функция раскладки графа */
export function layoutGraph(
  commits: Record<string, CommitNode>,
  branches: Record<string, string>,
  headSha: string | null,
  currentBranch: string | null,
  config: Partial<GraphConfig> = {}
): GraphState {
  const fullConfig: GraphConfig = { ...DEFAULT_GRAPH_CONFIG, ...config };

  if (!headSha || Object.keys(commits).length === 0) {
    return {
      nodes: new Map(),
      edges: [],
      branches: new Map(),
      headSha: null,
      currentBranch: null,
    };
  }

  // Топологическая сортировка
  const sortedCommits = topologicalSort(commits, headSha);

  // Назначаем ветки коммитам
  const commitBranches = assignBranchesToCommits(sortedCommits, branches, headSha, currentBranch);

  // Вычисляем позиции
  const positions = calculatePositions(sortedCommits, commitBranches, branches, fullConfig);

  // Создаём рёбра
  const edges = createEdges(sortedCommits, positions, commitBranches);

  // Создаём узлы
  const nodes = new Map<string, CommitNodeVisual>();
  const branchLabelsMap = new Map<string, string[]>();

  // Собираем метки веток для каждого коммита
  for (const [branchName, sha] of Object.entries(branches)) {
    if (!branchLabelsMap.has(sha)) {
      branchLabelsMap.set(sha, []);
    }
    branchLabelsMap.get(sha)!.push(branchName);
  }

  for (const commit of sortedCommits) {
    const position = positions.get(commit.sha)!;
    const branchInfo = commitBranches.get(commit.sha) || null;
    const labels = branchLabelsMap.get(commit.sha) || [];

    nodes.set(commit.sha, {
      commit,
      position,
      branchInfo,
      isHead: commit.sha === headSha,
      isMerge: commit.parents.length > 1,
      branchLabels: labels,
    });
  }

  // Создаём информацию о ветках
  const branchesInfo = new Map<string, BranchInfo>();
  for (const branchName of Object.keys(branches)) {
    branchesInfo.set(branchName, createBranchInfo(branchName));
  }

  return {
    nodes,
    edges,
    branches: branchesInfo,
    headSha,
    currentBranch,
  };
}

/** Вычисляет размер SVG контейнера на основе графа */
export function calculateGraphSize(
  graphState: GraphState,
  config: Partial<GraphConfig> = {}
): { width: number; height: number } {
  const fullConfig: GraphConfig = { ...DEFAULT_GRAPH_CONFIG, ...config };

  if (graphState.nodes.size === 0) {
    return { width: fullConfig.width, height: fullConfig.height };
  }

  let maxX = 0;
  let maxY = 0;

  for (const node of graphState.nodes.values()) {
    maxX = Math.max(maxX, node.position.x + fullConfig.nodeRadius * 2);
    maxY = Math.max(maxY, node.position.y + fullConfig.nodeRadius * 2);
  }

  return {
    width: Math.max(fullConfig.width, maxX + fullConfig.paddingLeft),
    height: Math.max(fullConfig.height, maxY + fullConfig.paddingTop),
  };
}
